import { useEffect, useState } from 'react'
import { LuArrowRight, LuArrowLeft } from 'react-icons/lu'
import { Link, LinkProps, useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import useOnboardFrom from '../../hooks/useOnboardFrom'

interface Props {
  next?: LinkProps['to']
  prev?: LinkProps['to']
  auto?: unknown[]
  skip?: boolean
  label?: string
  disabled?: boolean
  onNext?: () => Promise<void>
}

export default function NextPrev({ next, prev, auto, skip, label, disabled, onNext }: Props) {
  const [nextOk, setNextOk] = useState(!!auto?.length)
  const navigate = useNavigate()

  // The page AppLayout took the user away from. Every link below carries it to the
  // next step — a Link starts a fresh history entry, so state that is not passed
  // along is gone by step two — and the last step, the one with no `next`, spends
  // it. Nobody was detoured, nobody set it: the Done button goes home as before.
  // `to` consumes only pathname/search/hash, so the destination's own state is
  // handed back separately or a page that reads it (FileBrowser's backTo) returns
  // degraded.
  const from = useOnboardFrom()

  useEffect(() => {
    console.log('nextOk', !!auto?.length, 'onNext', onNext)
    setNextOk(!!auto?.length)
  }, [auto])

  // ⚠ A STEP THAT FAILED MUST NOT ADVANCE (app#296). `useNetwork.add` throws now rather than
  // alerting, so a refused creation reaches here as a rejection; the step renders the refusal in
  // its own surface, and staying put is what makes that reachable.
  //
  // ⚠⚠ IT LOGS, BECAUSE NOT EVERY `onNext` HAS A SURFACE OF ITS OWN. `onboard/Network` renders
  // the refusal before it re-throws, but `onboard/Welcome`'s `onNext` awaits `save` bare — so a
  // failed profile save reaches here as the only trace there is. Catching without logging turned
  // that into a Next button that does nothing and says nothing, in the console included; the
  // unhandled rejection this replaced at least printed. Same `console.error` shape the four
  // creation call sites use.
  const handleNext = async() => {
    if (!onNext) return
    try {
      await onNext()
    } catch (e) {
      console.error('onboarding step failed', e)
      return
    }
    if (next) navigate(next, { state: { from } })
  }

  return (
    <>
      {!!prev && (
        <Button
          to={prev}
          state={{ back: true, from }}
          size="small"
          startIcon={<LuArrowLeft />}
          component={Link}
          sx={{ color: 'contrast.medium', mr: 1 }}
        >
          Back
        </Button>
      )}
      {next ? (
        nextOk ? (
          <Button
            size="small"
            variant="contained"
            endIcon={<LuArrowRight />}
            to={next}
            state={{ from }}
            component={Link}
          >
            Next
          </Button>
        ) : (
          <Button
            {...(onNext ? {} : { to: next, state: { from }, component: Link })}
            size="small"
            variant={disabled ? 'text' : 'contained'}
            endIcon={disabled ? undefined : <LuArrowRight />}
            disabled={disabled}
            onClick={handleNext}
          >
            {label ?? 'Next'}
          </Button>
        )
      ) : (
        <Button
          to={from ?? '/'}
          state={from?.state}
          size="small"
          variant="contained"
          component={Link}
          disabled={disabled}
        >
          Done
        </Button>
      )}
      {skip && next && (
        <Button
          to={next}
          state={{ from }}
          size="small"
          component={Link}
          endIcon={<LuArrowRight />}
          sx={{ color: 'contrast.medium', ml: 1 }}
        >
          Skip
        </Button>
      )}
    </>
  )
}
